import React from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, XCircle } from 'lucide-react';
export function ProblemSection() {
  const painPoints = ['I should be hitting the ball harder than this.', "I feel strong… but the ball doesn't jump.", "I don't know if I need more lifting, more swings, or a new approach.", 'My best contact shows up… and then disappears.'];
  const failures = [{
    title: 'More lifting makes tired power',
    desc: 'Tired power looks good in the gym and disappears in games.'
  }, {
    title: "Random explosive workouts don't build a pattern",
    desc: 'You get a good session… then nothing to build on.'
  }, {
    title: 'Conditioning can flatten your fast twitch',
    desc: 'You last longer — but you hit softer.'
  }, {
    title: "Mechanics tweaks don't survive pressure",
    desc: "You can't 'cue' your way into nervous system output."
  }];
  return <section className="py-24 bg-[#0a0f1a] text-white relative overflow-hidden" data-id="element-62">
      {/* Texture overlay */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
      backgroundImage: 'radial-gradient(#ffffff 1px, transparent 1px)',
      backgroundSize: '32px 32px'
    }} data-id="element-63"></div>

      <div className="container mx-auto px-4 relative z-10" data-id="element-64">
        {/* The Frustrating Truth */}
        <div className="max-w-3xl mx-auto mb-20" data-id="element-65">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-12" data-id="element-66">
            <h2 className="text-amber-500 font-bold tracking-widest uppercase text-sm mb-4" data-id="element-67">
              The Frustrating Truth Nobody Says Out Loud
            </h2>
            <p className="text-3xl md:text-4xl font-bold leading-tight text-white mb-8" data-id="element-68">
              In college baseball, being "strong" isn't rare anymore.{' '}
              <span className="text-gray-500" data-id="element-69">A lot of guys are strong.</span>
            </p>
            <div className="text-lg text-gray-300 space-y-4 text-left bg-white/5 p-8 rounded-lg border border-white/10" data-id="element-70">
              <p data-id="element-71">
                But <strong className="text-white" data-id="element-72">dangerous</strong>? Dangerous
                is different.
              </p>
              <p data-id="element-73">
                Dangerous is the hitter who can access power{' '}
                <strong className="text-blue-400" data-id="element-74">on command</strong>: on the
                road, in cold weather, after travel, when legs are cooked, when
                timing isn't perfect.
              </p>
              <p className="text-xl font-semibold text-white pt-2" data-id="element-75">
                Most training makes you tougher. It doesn't make you available.
              </p>
            </div>
          </motion.div>

          {/* Pain Points Grid */}
          <div className="grid md:grid-cols-2 gap-4 mb-16" data-id="element-76">
            {painPoints.map((point, i) => <motion.div key={i} initial={{
            opacity: 0,
            x: -10
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="flex items-start gap-3 p-4 bg-red-500/5 border border-red-500/20 rounded-lg" data-id="element-77">
                <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" data-id="element-78" />
                <p className="text-gray-200 font-medium" data-id="element-79">"{point}"</p>
              </motion.div>)}
          </div>
        </div>

        {/* Why Old Solutions Fail */}
        <div className="max-w-5xl mx-auto" data-id="element-80">
          <div className="text-center mb-12" data-id="element-81">
            <h3 className="text-2xl font-bold text-white" data-id="element-82">
              Why what you tried didn't stick
            </h3>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-83">
            {failures.map((fail, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="bg-[#0f1623] p-6 rounded-xl border border-white/5 hover:border-white/10 transition-colors" data-id="element-84">
                <div className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center mb-4" data-id="element-85">
                  <span className="text-gray-400 font-bold" data-id="element-86">{i + 1}</span>
                </div>
                <h4 className="text-lg font-bold text-white mb-2" data-id="element-87">
                  {fail.title}
                </h4>
                <p className="text-sm text-gray-400" data-id="element-88">{fail.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </div>
    </section>;
}